import type * as geojson from 'geojson';
import {omit} from 'lodash';
import {UseQueryResult, useQuery} from 'react-query';

import {recordEvent} from 'src/components/Analytics';
import {
  ApiForecastTimeSeries,
  ForecastTimeSeriesQuery,
  batchFetchForecastTimeSeries,
} from 'src/lib/api/series';
import {GridSampling, isDynamicalSource} from 'src/lib/dynamical/datasets';
import {fetchDynamicalForecastTimeSeries} from 'src/lib/dynamical/fetchForecast';

export type DynamicalDirectOptions = {
  geometryBySiteId: {[siteId: string]: geojson.Geometry | undefined};
  localTimeZone: string;
  /** Which API pipeline's grid semantics to reproduce — see GridSampling. */
  sampling: GridSampling;
};

export type ForecastTimeSeriesOptions = {
  isStaging?: boolean;
  contextOrgId?: string;
  /**
   * When set, queries for sources hosted on dynamical.org are read directly from
   * its icechunk stores; anything the direct path can't serve (other sources,
   * missing geometry/init times, or any error) is fetched from the HF API.
   */
  dynamicalDirect?: DynamicalDirectOptions | null;
  /**
   * Hold the query without fetching (react-query enabled flag). Used while the
   * geometry needed for direct reads is still resolving: starting in API mode
   * and flipping to direct mode later would double-fetch and blank the plot on
   * the query-key change.
   */
  enabled?: boolean;
};

function isDirectEligible(
  query: ForecastTimeSeriesQuery,
  dynamicalDirect: DynamicalDirectOptions
): boolean {
  return (
    isDynamicalSource(query.source) &&
    !query.includeFlowContributingAreas &&
    !!query.initializationTimes?.length &&
    !!dynamicalDirect.geometryBySiteId[query.siteId]
  );
}

async function fetchWithDynamicalDirect(
  queries: ForecastTimeSeriesQuery[],
  dynamicalDirect: DynamicalDirectOptions,
  apiOptions: {isStaging: boolean; contextOrgId?: string}
): Promise<ApiForecastTimeSeries[]> {
  const results = new Array<ApiForecastTimeSeries | null>(queries.length).fill(null);

  const directIndices: number[] = [];
  const apiIndices: number[] = [];
  queries.forEach((query, index) =>
    (isDirectEligible(query, dynamicalDirect) ? directIndices : apiIndices).push(index)
  );

  // Ineligible (non-dynamical-source) queries go to the API immediately — they
  // must not wait on multi-second browser zarr reads they take no part in. The
  // early no-op catch keeps a rejection from surfacing as unhandled before the
  // await below observes it.
  const apiBatch =
    apiIndices.length > 0
      ? batchFetchForecastTimeSeries(
          apiIndices.map((index) => queries[index]),
          apiOptions
        )
      : Promise.resolve([]);
  apiBatch.catch(() => {});

  const directResults = await Promise.allSettled(
    directIndices.map((index) =>
      fetchDynamicalForecastTimeSeries(
        queries[index],
        dynamicalDirect.geometryBySiteId[queries[index].siteId]!,
        dynamicalDirect.localTimeZone,
        dynamicalDirect.sampling
      )
    )
  );

  const fallbackIndices: number[] = [];
  directResults.forEach((result, directIndex) => {
    const queryIndex = directIndices[directIndex];
    if (result.status === 'fulfilled') {
      results[queryIndex] = result.value;
    } else {
      fallbackIndices.push(queryIndex);
      const reason =
        result.reason instanceof Error ? result.reason.message : String(result.reason);
      console.warn('dynamical direct read failed, falling back to API:', reason);
      recordEvent('dynamical_direct_fallback', {source: queries[queryIndex].source, reason});
    }
  });

  const [apiResults, fallbackResults] = await Promise.all([
    apiBatch,
    fallbackIndices.length > 0
      ? batchFetchForecastTimeSeries(
          fallbackIndices.map((index) => queries[index]),
          apiOptions
        )
      : Promise.resolve([]),
  ]);
  apiIndices.forEach((queryIndex, apiIndex) => {
    results[queryIndex] = apiResults[apiIndex];
  });
  fallbackIndices.forEach((queryIndex, fallbackIndex) => {
    results[queryIndex] = fallbackResults[fallbackIndex];
  });

  return results.filter((result): result is ApiForecastTimeSeries => result !== null);
}

export default function useForecastTimeSeries(
  queries: ForecastTimeSeriesQuery[],
  options: ForecastTimeSeriesOptions = {}
): UseQueryResult<ApiForecastTimeSeries[]> {
  const {isStaging = false, contextOrgId, dynamicalDirect, enabled = true} = options;
  return useQuery(
    [
      'queries',
      queries,
      isStaging,
      contextOrgId,
      dynamicalDirect
        ? {
            sampling: dynamicalDirect.sampling,
            localTimeZone: dynamicalDirect.localTimeZone,
            geometryBySiteId: dynamicalDirect.geometryBySiteId,
          }
        : null,
    ],
    () => {
      // Filter out sourceMetadata from each query
      // sourceMetadata is used to inform the frontend about model details in the /projects
      // endpoint, but it doesn't specify anything on the queries.
      const filteredQueries = queries.map((query) => {
        return omit(query, 'sourceMetadata');
      });

      if (dynamicalDirect) {
        return fetchWithDynamicalDirect(filteredQueries, dynamicalDirect, {
          isStaging,
          contextOrgId,
        });
      }
      return batchFetchForecastTimeSeries(filteredQueries, {isStaging, contextOrgId});
    },
    {enabled: enabled && queries.length > 0}
  );
}
